import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Download, Loader2, Search, CheckCircle, Clock, Eye,
  Sparkles, FileText, Settings, XCircle, Paperclip, User,
  Trash2, Send, MessageSquare, Phone, ArrowRight,
  AlertTriangle, AlertCircle, FolderOpen, UserPlus, Link2, SkipForward,
  RefreshCw, Calendar
} from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import EstadisticasClientes from './EstadisticasClientes';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { autoClientesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const ESTADOS = {
  descargado: { label: 'Sin procesar', color: 'bg-blue-100 text-blue-700', icon: Download },
  procesando_ia: { label: 'Procesando IA', color: 'bg-purple-100 text-purple-700', icon: Sparkles },
  pendiente_revision: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  tramitado: { label: 'Tramitado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  descartado: { label: 'Descartado', color: 'bg-gray-100 text-gray-500', icon: XCircle },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const CLASIFICACIONES = {
  nuevo_cliente: { label: 'Cliente nuevo', color: 'bg-emerald-100 text-emerald-700', icon: UserPlus },
  cliente_existente: { label: 'Cliente existente', color: 'bg-blue-100 text-blue-700', icon: User },
  expediente_activo: { label: 'Expediente activo', color: 'bg-violet-100 text-violet-700', icon: FolderOpen },
};

export default function EmailsClientes() {
  const { user } = useAuth();

  // Helper para verificar sub-permisos de automatizacion clientes
  const hasSubPermiso = (subId, tipo = 'lectura') => {
    if (!user?.permisos) return true;
    if (user.rol === 'admin') return true;
    const p = user.permisos[subId];
    if (!p) return false;
    if (typeof p === 'boolean') return p;
    return !!p[tipo];
  };

  const canEmails = hasSubPermiso('automatizacion-clientes-emails');
  const canEstadisticas = hasSubPermiso('automatizacion-clientes-estadisticas');
  const canConfig = hasSubPermiso('automatizacion-clientes-config');

  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [processingAll, setProcessingAll] = useState(false);
  const [processAllJobId, setProcessAllJobId] = useState(null);
  const [processAllProgress, setProcessAllProgress] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [emailSubTab, setEmailSubTab] = useState('sin_procesar');
  const [totalEmails, setTotalEmails] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 100;

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, email: null });
  const [detailLoading, setDetailLoading] = useState(false);
  const [processingIA, setProcessingIA] = useState(null);

  // Tramitar dialog
  const [tramitarDialog, setTramitarDialog] = useState({ open: false, email: null });
  const [tramitarForm, setTramitarForm] = useState({
    tipo: 'nueva_consulta',
    cliente_nombre: '', cliente_apellidos: '', cliente_email: '', cliente_telefono: '',
    crear_contacto: true, asunto_consulta: '', descripcion_consulta: '', prioridad: 'media',
    consulta_id: null, expediente_id: null, titulo_actuacion: '', contacto_id: null, notas: '',
    sin_respuesta: false,
  });
  const [tramitando, setTramitando] = useState(false);

  // Responder dialog
  const [responderDialog, setResponderDialog] = useState({ open: false, email: null });
  const [responderForm, setResponderForm] = useState({
    tipo_respuesta: 'email', destinatario_email: '', asunto: '', cuerpo: '',
    telefono_contacto: '', duracion_llamada: '', enviar_ahora: true,
  });
  const [respondiendo, setRespondiendo] = useState(false);
  const [adjuntosRespuesta, setAdjuntosRespuesta] = useState([]);
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false);

  // Plantillas
  const [plantillas, setPlantillas] = useState([]);

  // Config dialog
  const [configDialog, setConfigDialog] = useState(false);
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    nombre: 'Emails Clientes', imap_host: 'mail.lexduo.cat', imap_port: 993,
    imap_user: 'info@lexduo.cat', imap_password: '', imap_use_ssl: true,
    imap_carpeta: 'INBOX', auto_fetch: false, auto_fetch_intervalo_min: 15, auto_process_ia: true,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  // Fetch dialog
  const [fetchDialog, setFetchDialog] = useState(false);
  const [fetchForm, setFetchForm] = useState({
    fecha_desde: new Date().toISOString().split('T')[0],
    fecha_hasta: new Date().toISOString().split('T')[0],
    hora_desde: '', hora_hasta: '', remitente: '', limit: 50,
  });

  // Search helpers
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [expSearch, setExpSearch] = useState('');
  const [expResults, setExpResults] = useState([]);
  const [consultaSearch, setConsultaSearch] = useState('');
  const [consultaResults, setConsultaResults] = useState([]);

  // Discard confirm
  const [discardConfirm, setDiscardConfirm] = useState(null);

  // Map sub-tabs to estado filter
  const getFilterParams = useCallback(() => {
    const params = { limit: PAGE_SIZE, offset: (currentPage - 1) * PAGE_SIZE };
    if (emailSubTab === 'sin_procesar') params.estado = 'descargado';
    else if (emailSubTab === 'pendientes') params.estado = 'pendiente_revision';
    else if (emailSubTab === 'tramitados') params.estado = 'tramitado';
    else if (emailSubTab === 'descartados') params.estado = 'descartado';
    else if (emailSubTab === 'errores') params.estado = 'error';
    if (searchQuery) params.buscar = searchQuery;
    return params;
  }, [emailSubTab, searchQuery, currentPage]);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await autoClientesAPI.listEmails(getFilterParams());
      setEmails(res.data.emails || []);
      setTotalEmails(res.data.total || 0);
    } catch (err) {
      console.error('Error cargando emails clientes:', err);
    } finally {
      setLoading(false);
    }
  }, [getFilterParams]);

  const loadStats = useCallback(async () => {
    try {
      const res = await autoClientesAPI.getStats();
      setStats(res.data);
    } catch (err) {
      console.error('Error cargando stats:', err);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await autoClientesAPI.getConfig();
      if (res.data.config) {
        setConfig(res.data.config);
        setConfigForm({
          nombre: res.data.config.nombre || 'Emails Clientes',
          imap_host: res.data.config.imap_host || 'mail.lexduo.cat',
          imap_port: res.data.config.imap_port || 993,
          imap_user: res.data.config.imap_user || 'info@lexduo.cat',
          imap_password: '',
          imap_use_ssl: res.data.config.imap_use_ssl !== false,
          imap_carpeta: res.data.config.imap_carpeta || 'INBOX',
          auto_fetch: res.data.config.auto_fetch || false,
          auto_fetch_intervalo_min: res.data.config.auto_fetch_intervalo_min || 15,
          auto_process_ia: res.data.config.auto_process_ia !== false,
        });
      }
    } catch (err) {
      console.error('Error cargando config:', err);
    }
  }, []);

  useEffect(() => { loadEmails(); loadStats(); loadConfig(); loadPlantillas(); }, [loadEmails, loadStats, loadConfig]);

  const loadPlantillas = async () => {
    try {
      const res = await autoClientesAPI.getPlantillas();
      setPlantillas(res.data.plantillas || []);
    } catch { /* ignore */ }
  };

  // ===== HANDLERS =====

  const handleFetchEmails = () => {
    if (!config) { toast({ title: 'Configure primero la cuenta de email', variant: 'destructive' }); setConfigDialog(true); return; }
    setFetchDialog(true);
  };

  const executeFetch = async () => {
    setFetching(true);
    setFetchDialog(false);
    try {
      const body = { limit: fetchForm.limit || 50 };
      if (fetchForm.fecha_desde) body.fecha_desde = fetchForm.fecha_desde;
      if (fetchForm.fecha_hasta) body.fecha_hasta = fetchForm.fecha_hasta;
      if (fetchForm.hora_desde) body.hora_desde = fetchForm.hora_desde;
      if (fetchForm.hora_hasta) body.hora_hasta = fetchForm.hora_hasta;
      if (fetchForm.remitente) body.remitente = fetchForm.remitente;
      const res = await autoClientesAPI.fetchEmails(body);
      const d = res.data;
      toast({ title: 'Descarga completada', description: `${d.nuevos} nuevos, ${d.duplicados} duplicados` });
      loadEmails(); loadStats();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error descargando', variant: 'destructive' });
    } finally { setFetching(false); }
  };

  const handleProcessIA = async (emailId) => {
    setProcessingIA(emailId);
    try {
      const res = await autoClientesAPI.processIA(emailId);
      toast({ title: 'Analisis IA completado', description: `Clasificacion: ${res.data.clasificacion}` });
      loadEmails(); loadStats();
      if (detailDialog.open && detailDialog.email?.id === emailId) {
        const detail = await autoClientesAPI.getEmail(emailId);
        setDetailDialog({ open: true, email: detail.data });
      }
    } catch (err) {
      toast({ title: 'Error IA', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setProcessingIA(null); }
  };

  const handleProcessAll = async () => {
    setProcessingAll(true);
    try {
      const idsToProcess = selectedEmails.size > 0 ? Array.from(selectedEmails) : null;
      const res = await autoClientesAPI.processAll(idsToProcess);
      if (res.data.job_id) {
        setProcessAllJobId(res.data.job_id);
        pollProcessAll(res.data.job_id);
      } else {
        toast({ title: res.data.message });
        setProcessingAll(false);
      }
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
      setProcessingAll(false);
    }
  };

  const pollProcessAll = (jobId) => {
    let fallosSeguidos = 0;
    const interval = setInterval(async () => {
      try {
        const res = await autoClientesAPI.processAllProgress(jobId);
        fallosSeguidos = 0;
        setProcessAllProgress(res.data);
        if (res.data.estado === 'completado' || res.data.estado === 'error' || res.data.estado === 'no_encontrado') {
          clearInterval(interval);
          setProcessingAll(false);
          setProcessAllJobId(null);
          setSelectedEmails(new Set());
          loadEmails(); loadStats();
          toast({ title: 'Proceso completado', description: `${res.data.exitosos || 0} procesados, ${res.data.errores || 0} errores` });
        }
      } catch {
        // Un 429 del rate limit o un corte puntual no debe cortar el aviso:
        // el lote sigue procesandose en el servidor.
        fallosSeguidos += 1;
        if (fallosSeguidos >= 10) {
          clearInterval(interval);
          setProcessingAll(false);
          loadEmails(); loadStats();
        }
      }
    }, 3000);
  };

  const handleCancelProcessAll = async () => {
    if (processAllJobId) {
      await autoClientesAPI.processAllCancel(processAllJobId);
      toast({ title: 'Cancelado' });
    }
  };

  const openDetail = async (emailItem) => {
    setDetailLoading(true);
    setDetailDialog({ open: true, email: emailItem });
    try {
      const res = await autoClientesAPI.getEmail(emailItem.id);
      setDetailDialog({ open: true, email: res.data });
    } catch (err) {
      toast({ title: 'Error cargando detalle', variant: 'destructive' });
    } finally { setDetailLoading(false); }
  };

  const openTramitar = (emailItem) => {
    const datos = emailItem.ia_datos_cliente || {};
    setTramitarForm({
      tipo: emailItem.ia_clasificacion === 'expediente_activo' ? 'expediente_activo' :
            emailItem.ia_clasificacion === 'cliente_existente' ? 'consulta_existente' : 'nueva_consulta',
      cliente_nombre: datos.nombre || emailItem.remitente_nombre || '',
      cliente_apellidos: datos.apellidos || '',
      cliente_email: datos.email || emailItem.remitente || '',
      cliente_telefono: datos.telefono || '',
      crear_contacto: !emailItem.contacto_id,
      asunto_consulta: emailItem.asunto || '',
      descripcion_consulta: '',
      prioridad: emailItem.ia_prioridad || 'media',
      consulta_id: emailItem.consulta_id,
      expediente_id: emailItem.expediente_id,
      titulo_actuacion: `Email cliente: ${(emailItem.asunto || '').substring(0, 200)}`,
      contacto_id: emailItem.contacto_id,
      notas: '',
      sin_respuesta: false,
    });
    setTramitarDialog({ open: true, email: emailItem });
  };

  const handleTramitar = async () => {
    setTramitando(true);
    try {
      const res = await autoClientesAPI.tramitarEmail(tramitarDialog.email.id, tramitarForm);
      toast({ title: 'Email tramitado', description: res.data.message });
      const sinRespuesta = tramitarForm.sin_respuesta;
      setTramitarDialog({ open: false, email: null });
      loadEmails(); loadStats();
      // Solo abrir compositor si NO marcó "sin respuesta" y NO se ha respondido ya
      if (!sinRespuesta) {
        const updatedEmail = await autoClientesAPI.getEmail(res.data.consulta_id ? tramitarDialog.email.id : tramitarDialog.email.id);
        if (!updatedEmail.data.respondido) {
          openResponder(updatedEmail.data);
        }
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setTramitando(false); }
  };

  const openResponder = (emailItem) => {
    // Si hay tipo_consulta de IA, buscar la plantilla correspondiente
    let cuerpoInicial = emailItem.ia_respuesta_sugerida || '';
    let asuntoInicial = emailItem.ia_asunto_sugerido || `Re: ${emailItem.asunto || ''}`;
    const tipoConsulta = emailItem.ia_tipo_consulta || '';

    // Si la IA no genero respuesta, usar plantilla por tipo
    if (!cuerpoInicial && tipoConsulta) {
      const tpl = plantillas.find(p => p.key === tipoConsulta);
      if (tpl) {
        const nombre = emailItem.remitente_nombre || emailItem.remitente || 'Sr./Sra.';
        cuerpoInicial = tpl.cuerpo.replace(/\{\{cliente_nombre\}\}/g, nombre).replace(/\{\{asunto_original\}\}/g, emailItem.asunto || '');
        asuntoInicial = tpl.asunto.replace(/\{\{asunto_original\}\}/g, emailItem.asunto || '');
      }
    }

    setResponderForm({
      tipo_respuesta: 'email',
      destinatario_email: emailItem.remitente || '',
      asunto: asuntoInicial,
      cuerpo: cuerpoInicial,
      telefono_contacto: '', duracion_llamada: '', enviar_ahora: true,
    });
    setResponderDialog({ open: true, email: emailItem });
    setAdjuntosRespuesta([]);
  };

  const handleResponder = async () => {
    setRespondiendo(true);
    try {
      const payload = {
        ...responderForm,
        adjuntos_keys: adjuntosRespuesta.map(a => a.s3_key).filter(Boolean),
      };
      const res = await autoClientesAPI.responderEmail(responderDialog.email.id, payload);
      toast({ title: res.data.message || 'Respuesta registrada' });
      setResponderDialog({ open: false, email: null });
      setAdjuntosRespuesta([]);
      loadEmails(); loadStats();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setRespondiendo(false); }
  };

  const handleUploadAdjunto = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setSubiendoAdjunto(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await autoClientesAPI.uploadAdjunto(formData);
        if (res.data.success) {
          setAdjuntosRespuesta(prev => [...prev, {
            nombre: file.name,
            tamano: file.size,
            tipo: file.type,
            s3_key: res.data.s3_key,
          }]);
        }
      }
    } catch (err) {
      toast({ title: 'Error subiendo archivo', variant: 'destructive' });
    } finally {
      setSubiendoAdjunto(false);
      e.target.value = '';
    }
  };

  const handleDiscard = async (emailId) => {
    try {
      await autoClientesAPI.discardEmail(emailId);
      toast({ title: 'Email descartado' });
      setDiscardConfirm(null);
      loadEmails(); loadStats();
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Eliminar este email definitivamente?')) return;
    try {
      await autoClientesAPI.deleteEmail(emailId);
      toast({ title: 'Email eliminado definitivamente' });
      loadEmails(); loadStats();
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await autoClientesAPI.saveConfig(configForm);
      toast({ title: 'Configuracion guardada' });
      loadConfig();
      setConfigDialog(false);
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally { setSavingConfig(false); }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const payload = { ...configForm };
      if (!payload.imap_password && config) payload.imap_password = '';
      const res = await autoClientesAPI.testConnection(payload);
      toast({ title: res.data.success ? 'Conexion exitosa' : 'Error', description: res.data.message, variant: res.data.success ? 'default' : 'destructive' });
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally { setTestingConnection(false); }
  };

  // Search helpers
  useEffect(() => {
    if (contactSearch.length < 2) { setContactResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await autoClientesAPI.buscarContactos(contactSearch);
        setContactResults(res.data.contactos || []);
      } catch { setContactResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [contactSearch]);

  useEffect(() => {
    if (expSearch.length < 2) { setExpResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await autoClientesAPI.buscarExpedientes(expSearch);
        setExpResults(res.data.expedientes || []);
      } catch { setExpResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [expSearch]);

  useEffect(() => {
    if (consultaSearch.length < 2 && !tramitarForm.contacto_id) { setConsultaResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await autoClientesAPI.buscarConsultas(consultaSearch, tramitarForm.contacto_id);
        setConsultaResults(res.data.consultas || []);
      } catch { setConsultaResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [consultaSearch, tramitarForm.contacto_id]);

  // ===== SUB-TABS =====
  const subTabs = [
    ...(canEmails ? [
      { key: 'sin_procesar', label: 'Sin procesar', count: stats?.sin_procesar },
      { key: 'pendientes', label: 'Pendientes', count: stats?.pendientes },
      { key: 'tramitados', label: 'Tramitados', count: stats?.tramitados },
      { key: 'descartados', label: 'Descartados', count: stats?.descartados },
      { key: 'errores', label: 'Errores', count: stats?.errores },
      { key: 'todos', label: 'Todos', count: stats?.total },
    ] : []),
    ...(canEstadisticas ? [
      { key: 'estadisticas', label: 'Estadisticas', count: null },
    ] : []),
  ];

  // ===== RENDER =====
  return (
    <div className="space-y-4" data-testid="emails-clientes-page">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="border-l-4 border-l-blue-500"><CardContent className="p-3">
            <div className="text-xs text-gray-500">Total</div>
            <div className="text-lg font-bold">{stats.total}</div>
          </CardContent></Card>
          <Card className="border-l-4 border-l-indigo-500"><CardContent className="p-3">
            <div className="text-xs text-gray-500">Sin procesar</div>
            <div className="text-lg font-bold text-indigo-600">{stats.sin_procesar}</div>
          </CardContent></Card>
          <Card className="border-l-4 border-l-amber-500"><CardContent className="p-3">
            <div className="text-xs text-gray-500">Pendientes</div>
            <div className="text-lg font-bold text-amber-600">{stats.pendientes}</div>
          </CardContent></Card>
          <Card className={`border-l-4 border-l-red-500 ${stats.urgentes > 0 ? 'ring-1 ring-red-300' : ''}`}><CardContent className="p-3">
            <div className="text-xs text-gray-500">Urgentes</div>
            <div className={`text-lg font-bold text-red-600 ${stats.urgentes > 0 ? 'animate-pulse' : ''}`}>{stats.urgentes}</div>
          </CardContent></Card>
          <Card className="border-l-4 border-l-green-500"><CardContent className="p-3">
            <div className="text-xs text-gray-500">Tramitados</div>
            <div className="text-lg font-bold text-green-600">{stats.tramitados}</div>
          </CardContent></Card>
          <Card className="border-l-4 border-l-teal-500"><CardContent className="p-3">
            <div className="text-xs text-gray-500">Respondidos</div>
            <div className="text-lg font-bold text-teal-600">{stats.respondidos}</div>
          </CardContent></Card>
          <Card className="border-l-4 border-l-gray-400"><CardContent className="p-3">
            <div className="text-xs text-gray-500">Descartados</div>
            <div className="text-lg font-bold text-gray-500">{stats.descartados}</div>
          </CardContent></Card>
        </div>
      )}

      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        {canEmails && (
        <Button size="sm" onClick={handleFetchEmails} disabled={fetching} data-testid="fetch-client-emails-btn">
          {fetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
          Descargar Emails
        </Button>
        )}
        {canEmails && emailSubTab === 'sin_procesar' && (
          <div className="flex items-center gap-2">
            {selectedEmails.size > 0 && (
              <Badge className="bg-purple-100 text-purple-700" data-testid="selected-count-clientes">
                {selectedEmails.size} sel.
              </Badge>
            )}
            <Button size="sm" variant="outline" onClick={handleProcessAll} disabled={processingAll || !stats?.sin_procesar} data-testid="process-all-client-btn">
              {processingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              {processingAll
                ? `Procesando... ${processAllProgress?.procesados || 0}/${processAllProgress?.total || '?'}`
                : selectedEmails.size > 0
                  ? `Procesar seleccionados (${selectedEmails.size})`
                  : 'Procesar todos con IA'}
            </Button>
          </div>
        )}
        {processingAll && (
          <Button size="sm" variant="destructive" onClick={handleCancelProcessAll}>
            <XCircle className="h-4 w-4 mr-1" /> Cancelar
          </Button>
        )}
        {canConfig && (
        <Button size="sm" variant="outline" onClick={() => setConfigDialog(true)} data-testid="config-client-btn">
          <Settings className="h-4 w-4 mr-1" /> Config
        </Button>
        )}
        <div className="flex-1" />
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
          <Input
            className="pl-8 h-9 w-60" placeholder="Buscar..."
            value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            data-testid="search-client-emails"
          />
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b pb-2">
        {subTabs.map(t => (
          <button key={t.key}
            className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${emailSubTab === t.key ? 'bg-teal-100 text-teal-800 border border-b-0 border-teal-300' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => { setEmailSubTab(t.key); setCurrentPage(1); }}
            data-testid={`subtab-client-${t.key}`}
          >
            {t.label} {t.count != null && <span className="ml-1 text-xs">({t.count})</span>}
          </button>
        ))}
      </div>

      {/* Estadisticas tab */}
      {emailSubTab === 'estadisticas' && (
        <EstadisticasClientes />
      )}

      {/* Email list */}
      {emailSubTab !== 'estadisticas' && (
        <>
        {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : emails.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Mail className="h-10 w-10 mx-auto mb-2 text-gray-300" />
          <p>No hay emails en esta seccion</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Select all bar for sin_procesar */}
          {emailSubTab === 'sin_procesar' && emails.length > 0 && (
            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg border text-sm">
              <input
                type="checkbox"
                checked={emails.filter(e => e.estado === 'descargado').length > 0 && emails.filter(e => e.estado === 'descargado').every(e => selectedEmails.has(e.id))}
                onChange={(e) => {
                  const descargados = emails.filter(em => em.estado === 'descargado').map(em => em.id);
                  if (e.target.checked) {
                    setSelectedEmails(new Set([...selectedEmails, ...descargados]));
                  } else {
                    const next = new Set(selectedEmails);
                    descargados.forEach(id => next.delete(id));
                    setSelectedEmails(next);
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 text-purple-600 cursor-pointer"
                data-testid="select-all-clientes"
              />
              <span className="text-gray-600">
                {selectedEmails.size > 0 ? `${selectedEmails.size} seleccionados` : 'Seleccionar todos'}
              </span>
              {selectedEmails.size > 0 && (
                <button className="text-xs text-gray-500 hover:text-gray-700 underline" onClick={() => setSelectedEmails(new Set())}>Limpiar</button>
              )}
            </div>
          )}
          {emails.map(em => {
            const est = ESTADOS[em.estado] || ESTADOS.descargado;
            const clas = CLASIFICACIONES[em.ia_clasificacion] || null;
            const EstIcon = est.icon;
            return (
              <Card key={em.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`email-client-${em.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {emailSubTab === 'sin_procesar' && em.estado === 'descargado' && (
                      <input
                        type="checkbox"
                        checked={selectedEmails.has(em.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          const next = new Set(selectedEmails);
                          if (e.target.checked) next.add(em.id);
                          else next.delete(em.id);
                          setSelectedEmails(next);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 mt-1 rounded border-gray-300 text-purple-600 cursor-pointer shrink-0"
                        data-testid={`select-client-email-${em.id}`}
                      />
                    )}
                    <div className="flex-1 min-w-0" onClick={() => openDetail(em)}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${est.color}`}><EstIcon className="h-3 w-3 mr-1" />{est.label}</Badge>
                        {clas && <Badge className={`text-xs ${clas.color}`}>{clas.label}</Badge>}
                        {em.ia_prioridad === 'urgente' && <Badge className="text-xs bg-red-100 text-red-700">URGENTE</Badge>}
                        {em.ia_prioridad === 'alta' && <Badge className="text-xs bg-orange-100 text-orange-700">ALTA</Badge>}
                        {em.ia_tipo_consulta && em.ia_tipo_consulta !== 'consulta_inicial' && (
                          <Badge className="text-xs bg-cyan-50 text-cyan-700 border border-cyan-200">{em.ia_tipo_consulta}</Badge>
                        )}
                        {em.respondido && <Badge className="text-xs bg-teal-100 text-teal-700">Respondido</Badge>}
                        {em.tiene_adjuntos && <Paperclip className="h-3 w-3 text-gray-400" />}
                        {em.estado === 'descargado' && em.error_mensaje && (
                          <Badge className="text-xs bg-amber-100 text-amber-700 border border-amber-300" title={em.error_mensaje}>
                            <AlertCircle className="h-3 w-3 mr-1" /> Intento anterior fallido
                          </Badge>
                        )}
                      </div>
                      <div className="font-medium text-sm truncate">{em.asunto || '(sin asunto)'}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                        <span>{em.remitente_nombre || em.remitente}</span>
                        {em.fecha_email && <span>{new Date(em.fecha_email).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
                        {em.contacto_nombre && <span className="text-blue-600">| {em.contacto_nombre}</span>}
                        {em.expediente_numero && <span className="text-violet-600">| EXP {em.expediente_numero}</span>}
                      </div>
                      {em.ia_resumen && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{em.ia_resumen}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {em.estado === 'descargado' && (
                        <>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleProcessIA(em.id); }}
                            disabled={processingIA === em.id} data-testid={`process-ia-${em.id}`}>
                            {processingIA === em.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); setDiscardConfirm(em.id); }}
                            data-testid={`discard-client-${em.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {em.estado === 'pendiente_revision' && (
                        <>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openResponder(em); }} data-testid={`responder-${em.id}`}>
                            <Send className="h-4 w-4 mr-1" /> Responder
                          </Button>
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); openTramitar(em); }} data-testid={`tramitar-${em.id}`}>
                            <ArrowRight className="h-4 w-4 mr-1" /> Tramitar
                          </Button>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); setDiscardConfirm(em.id); }}
                            data-testid={`discard-client-${em.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {em.estado === 'tramitado' && !em.respondido && (
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openResponder(em); }} data-testid={`responder-${em.id}`}>
                          <Send className="h-4 w-4 mr-1" /> Responder
                        </Button>
                      )}
                      {em.estado === 'error' && (
                        <>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleProcessIA(em.id); }}
                            disabled={processingIA === em.id} title="Reintentar analisis con IA" data-testid={`retry-ia-${em.id}`}>
                            {processingIA === em.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); setDiscardConfirm(em.id); }}
                            data-testid={`discard-client-${em.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {em.estado === 'descartado' && (
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(em.id); }}
                          data-testid={`delete-client-${em.id}`}>
                          <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalEmails > PAGE_SIZE && (
        <div className="flex justify-center gap-2 pt-2">
          <Button size="sm" variant="outline" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
          <span className="text-sm self-center text-gray-500">Pag {currentPage} de {Math.ceil(totalEmails / PAGE_SIZE)}</span>
          <Button size="sm" variant="outline" disabled={currentPage >= Math.ceil(totalEmails / PAGE_SIZE)} onClick={() => setCurrentPage(p => p + 1)}>Siguiente</Button>
        </div>
      )}
        </>
      )}

      {/* ===== DETAIL DIALOG ===== */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, email: null })}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] h-[92vh] overflow-y-auto">
          {detailDialog.email && (() => {
            const em = detailDialog.email;
            const est = ESTADOS[em.estado] || ESTADOS.descargado;
            const clas = CLASIFICACIONES[em.ia_clasificacion] || null;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-base">
                    <Mail className="h-5 w-5 text-teal-500" />
                    {em.asunto || '(sin asunto)'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge className={est.color}>{est.label}</Badge>
                    {clas && <Badge className={clas.color}>{clas.label}</Badge>}
                    {em.ia_prioridad && <Badge className="bg-gray-100 text-gray-700">Prioridad: {em.ia_prioridad}</Badge>}
                    {em.respondido && <Badge className="bg-teal-100 text-teal-700">Respondido</Badge>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">De:</span> {em.remitente_nombre} &lt;{em.remitente}&gt;</div>
                    <div><span className="text-gray-500">Fecha:</span> {em.fecha_email ? new Date(em.fecha_email).toLocaleString('es-ES') : '-'}</div>
                    {em.contacto_nombre && <div><span className="text-gray-500">Contacto:</span> <span className="text-blue-600">{em.contacto_nombre}</span></div>}
                    {em.expediente_numero && <div><span className="text-gray-500">Expediente:</span> <span className="text-violet-600">EXP {em.expediente_numero}</span></div>}
                    {em.tipo_tramitacion && <div><span className="text-gray-500">Tramitacion:</span> {em.tipo_tramitacion}</div>}
                    {em.tramitado_por_nombre && <div><span className="text-gray-500">Tramitado por:</span> {em.tramitado_por_nombre}</div>}
                  </div>

                  {/* AI Analysis */}
                  {em.ia_resumen && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="text-xs font-semibold text-purple-700 mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Analisis IA (confianza: {Math.round((em.ia_confianza || 0) * 100)}%)</div>
                      <p className="text-sm text-gray-700">{em.ia_resumen}</p>
                      {em.ia_respuesta_sugerida && (
                        <div className="mt-2 pt-2 border-t border-purple-200">
                          <div className="text-xs font-semibold text-purple-600 mb-1">Respuesta sugerida:</div>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{em.ia_respuesta_sugerida}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email body */}
                  <div className="bg-white border rounded-lg p-3">
                    <div className="text-xs font-semibold text-gray-500 mb-2">Contenido del email</div>
                    {detailLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <div className="text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
                        {em.cuerpo_texto || em.cuerpo_html?.replace(/<[^>]+>/g, '') || '(sin contenido)'}
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  {em.adjuntos_json && em.adjuntos_json.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 mb-1">Adjuntos ({em.adjuntos_json.filter(a => a.tipo !== 'message/rfc822').length})</div>
                      <div className="flex flex-wrap gap-2">
                        {em.adjuntos_json.map((adj, idx) => adj.tipo !== 'message/rfc822' && (
                          <a key={idx} href={autoClientesAPI.getPreviewAdjuntoUrl(em.id, idx)} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 rounded px-2 py-1 transition-colors">
                            <Paperclip className="h-3 w-3" /> {adj.nombre} <span className="text-gray-400">({(adj.tamano / 1024).toFixed(0)}KB)</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Error previo / actual */}
                  {em.error_mensaje && (
                    em.estado === 'error' ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Error
                        </div>
                        <p className="text-sm text-red-600">{em.error_mensaje}</p>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" /> Intento anterior fallido
                        </div>
                        <p className="text-sm text-amber-700">{em.error_mensaje}</p>
                        <p className="text-xs text-amber-600 mt-1">Puedes reintentar el analisis con IA o tramitar el email manualmente.</p>
                      </div>
                    )
                  )}

                  {/* Respuesta enviada */}
                  {em.respondido && em.respuesta_cuerpo && (
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Send className="h-4 w-4 text-teal-600" />
                        <span className="text-sm font-semibold text-teal-800">
                          Respuesta enviada via {em.respuesta_tipo === 'email' ? 'Email' : em.respuesta_tipo === 'telefono' ? 'Telefono' : em.respuesta_tipo === 'whatsapp' ? 'WhatsApp' : em.respuesta_tipo || 'Email'}
                        </span>
                        {em.fecha_respuesta && (
                          <span className="text-xs text-teal-600 ml-auto">{new Date(em.fecha_respuesta).toLocaleString('es-ES')}</span>
                        )}
                      </div>
                      {em.respuesta_tipo === 'email' && (
                        <div className="text-xs text-teal-700 mb-2 space-y-0.5">
                          <div><span className="font-medium">Para:</span> {em.respuesta_destinatario}</div>
                          <div><span className="font-medium">Asunto:</span> {em.respuesta_asunto}</div>
                        </div>
                      )}
                      {em.respuesta_tipo !== 'email' && em.respuesta_destinatario && (
                        <div className="text-xs text-teal-700 mb-2">
                          <span className="font-medium">Contacto:</span> {em.respuesta_destinatario}
                        </div>
                      )}
                      <div className="bg-white border border-teal-100 rounded p-3 text-sm max-h-60 overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: em.respuesta_cuerpo }} />
                    </div>
                  )}
                </div>
                <DialogFooter className="flex gap-2">
                  {em.estado === 'descargado' && (
                    <>
                      <Button onClick={() => handleProcessIA(em.id)} disabled={processingIA === em.id}>
                        {processingIA === em.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                        Procesar con IA
                      </Button>
                      <Button variant="destructive" onClick={() => { setDetailDialog({ open: false, email: null }); setDiscardConfirm(em.id); }}>
                        <Trash2 className="h-4 w-4 mr-1" /> Descartar
                      </Button>
                    </>
                  )}
                  {em.estado === 'pendiente_revision' && (
                    <>
                      <Button variant="outline" onClick={() => { setDetailDialog({ open: false, email: null }); openResponder(em); }}>
                        <Send className="h-4 w-4 mr-1" /> Responder
                      </Button>
                      <Button onClick={() => { setDetailDialog({ open: false, email: null }); openTramitar(em); }}>
                        <ArrowRight className="h-4 w-4 mr-1" /> Tramitar
                      </Button>
                      <Button variant="destructive" onClick={() => { setDetailDialog({ open: false, email: null }); setDiscardConfirm(em.id); }}>
                        <Trash2 className="h-4 w-4 mr-1" /> Descartar
                      </Button>
                    </>
                  )}
                  {em.estado === 'tramitado' && !em.respondido && (
                    <Button variant="outline" onClick={() => { setDetailDialog({ open: false, email: null }); openResponder(em); }}>
                      <Send className="h-4 w-4 mr-1" /> Responder
                    </Button>
                  )}
                  {em.estado === 'error' && (
                    <>
                      <Button onClick={() => handleProcessIA(em.id)} disabled={processingIA === em.id}>
                        {processingIA === em.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                        Reintentar IA
                      </Button>
                      <Button variant="destructive" onClick={() => { setDetailDialog({ open: false, email: null }); setDiscardConfirm(em.id); }}>
                        <Trash2 className="h-4 w-4 mr-1" /> Descartar
                      </Button>
                    </>
                  )}
                  {em.estado === 'descartado' && (
                    <Button variant="destructive" onClick={() => { setDetailDialog({ open: false, email: null }); handleDelete(em.id); }}>
                      <Trash2 className="h-4 w-4 mr-1" /> Eliminar definitivamente
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ===== TRAMITAR DIALOG ===== */}
      <Dialog open={tramitarDialog.open} onOpenChange={(open) => !open && setTramitarDialog({ open: false, email: null })}>
        <DialogContent className="max-w-[90vw] w-[90vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-teal-500" /> Tramitar Email
            </DialogTitle>
          </DialogHeader>
          {tramitarDialog.email && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded p-3 text-sm">
                <div className="font-medium">{tramitarDialog.email.asunto}</div>
                <div className="text-gray-500">{tramitarDialog.email.remitente_nombre || tramitarDialog.email.remitente}</div>
              </div>

              {/* Tipo de tramitacion */}
              <div>
                <Label>Tipo de tramitacion</Label>
                <Select value={tramitarForm.tipo} onValueChange={(v) => setTramitarForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger data-testid="tramitar-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nueva_consulta"><UserPlus className="h-4 w-4 inline mr-1" />Nueva consulta (cliente nuevo o sin consulta)</SelectItem>
                    <SelectItem value="consulta_existente"><Link2 className="h-4 w-4 inline mr-1" />Vincular a consulta existente</SelectItem>
                    <SelectItem value="expediente_activo"><FolderOpen className="h-4 w-4 inline mr-1" />Registrar en expediente (actuacion)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* NUEVA CONSULTA */}
              {tramitarForm.tipo === 'nueva_consulta' && (
                <div className="space-y-3 bg-emerald-50 rounded-lg p-3">
                  <div className="text-sm font-semibold text-emerald-700">Datos del cliente</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Nombre</Label>
                      <Input value={tramitarForm.cliente_nombre} onChange={(e) => setTramitarForm(f => ({ ...f, cliente_nombre: e.target.value }))} data-testid="tramitar-nombre" />
                    </div>
                    <div>
                      <Label className="text-xs">Apellidos</Label>
                      <Input value={tramitarForm.cliente_apellidos} onChange={(e) => setTramitarForm(f => ({ ...f, cliente_apellidos: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input value={tramitarForm.cliente_email} onChange={(e) => setTramitarForm(f => ({ ...f, cliente_email: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Telefono</Label>
                      <Input value={tramitarForm.cliente_telefono} onChange={(e) => setTramitarForm(f => ({ ...f, cliente_telefono: e.target.value }))} />
                    </div>
                  </div>
                  {/* Contacto search */}
                  <div>
                    <Label className="text-xs">Vincular a contacto existente (opcional)</Label>
                    <Input placeholder="Buscar contacto..." value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} />
                    {contactResults.length > 0 && (
                      <div className="mt-1 border rounded bg-white max-h-32 overflow-y-auto">
                        {contactResults.map(c => (
                          <div key={c.id} className="px-2 py-1 text-sm hover:bg-gray-100 cursor-pointer flex justify-between"
                            onClick={() => {
                              setTramitarForm(f => ({
                                ...f, contacto_id: c.id, crear_contacto: false,
                                cliente_nombre: c.nombre || f.cliente_nombre,
                                cliente_apellidos: c.apellidos || f.cliente_apellidos,
                                cliente_email: c.email || f.cliente_email,
                                cliente_telefono: c.telefono || f.cliente_telefono,
                              }));
                              setContactSearch(''); setContactResults([]);
                            }}>
                            <span>{c.nombre} {c.apellidos}</span>
                            <span className="text-gray-400">{c.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {tramitarForm.contacto_id && <span className="text-xs text-green-600">Contacto vinculado (ID: {tramitarForm.contacto_id})</span>}
                  </div>
                  <div>
                    <Label className="text-xs">Asunto de la consulta</Label>
                    <Input value={tramitarForm.asunto_consulta} onChange={(e) => setTramitarForm(f => ({ ...f, asunto_consulta: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Prioridad</Label>
                    <Select value={tramitarForm.prioridad} onValueChange={(v) => setTramitarForm(f => ({ ...f, prioridad: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baja">Baja</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="urgente">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* CONSULTA EXISTENTE */}
              {tramitarForm.tipo === 'consulta_existente' && (
                <div className="space-y-3 bg-blue-50 rounded-lg p-3">
                  <div className="text-sm font-semibold text-blue-700">Buscar consulta existente</div>
                  <Input placeholder="Buscar por asunto o nombre de cliente..." value={consultaSearch} onChange={(e) => setConsultaSearch(e.target.value)} data-testid="buscar-consulta" />
                  {consultaResults.length > 0 && (
                    <div className="border rounded bg-white max-h-40 overflow-y-auto">
                      {consultaResults.map(q => (
                        <div key={q.id} className={`px-2 py-1.5 text-sm hover:bg-blue-50 cursor-pointer border-b ${tramitarForm.consulta_id === q.id ? 'bg-blue-100' : ''}`}
                          onClick={() => setTramitarForm(f => ({ ...f, consulta_id: q.id }))}>
                          <div className="font-medium">{q.asunto}</div>
                          <div className="text-xs text-gray-500">{q.cliente_nombre} | {q.estado} | {q.fecha ? new Date(q.fecha).toLocaleDateString('es-ES') : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {tramitarForm.consulta_id && <span className="text-xs text-blue-600">Consulta seleccionada: ID {tramitarForm.consulta_id}</span>}
                </div>
              )}

              {/* EXPEDIENTE ACTIVO */}
              {tramitarForm.tipo === 'expediente_activo' && (
                <div className="space-y-3 bg-violet-50 rounded-lg p-3">
                  <div className="text-sm font-semibold text-violet-700">Buscar expediente</div>
                  <Input placeholder="Buscar por numero de expediente..." value={expSearch} onChange={(e) => setExpSearch(e.target.value)} data-testid="buscar-expediente" />
                  {expResults.length > 0 && (
                    <div className="border rounded bg-white max-h-40 overflow-y-auto">
                      {expResults.map(e => (
                        <div key={e.id} className={`px-2 py-1.5 text-sm hover:bg-violet-50 cursor-pointer border-b ${tramitarForm.expediente_id === e.id ? 'bg-violet-100' : ''}`}
                          onClick={() => setTramitarForm(f => ({ ...f, expediente_id: e.id }))}>
                          <span className="font-medium">{e.numero_expediente}</span>
                          <span className="text-gray-400 ml-2">{e.estado}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {tramitarForm.expediente_id && <span className="text-xs text-violet-600">Expediente seleccionado: ID {tramitarForm.expediente_id}</span>}
                  <div>
                    <Label className="text-xs">Titulo de la actuacion</Label>
                    <Input value={tramitarForm.titulo_actuacion} onChange={(e) => setTramitarForm(f => ({ ...f, titulo_actuacion: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Notas */}
              <div>
                <Label className="text-xs">Notas internas (opcional)</Label>
                <Textarea value={tramitarForm.notas} onChange={(e) => setTramitarForm(f => ({ ...f, notas: e.target.value }))} rows={2} />
              </div>

              {/* Sin respuesta */}
              <div className="flex items-center gap-2 bg-gray-50 rounded p-3 border">
                <input type="checkbox" id="sin_respuesta" checked={tramitarForm.sin_respuesta}
                  onChange={(e) => setTramitarForm(f => ({ ...f, sin_respuesta: e.target.checked }))} />
                <Label htmlFor="sin_respuesta" className="text-sm cursor-pointer">
                  No requiere respuesta (solo tramitar y archivar)
                </Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTramitarDialog({ open: false, email: null })}>Cancelar</Button>
            <Button onClick={handleTramitar} disabled={tramitando} data-testid="tramitar-confirm-btn">
              {tramitando ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              Tramitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== RESPONDER DIALOG - Compositor completo ===== */}
      <Dialog open={responderDialog.open} onOpenChange={(open) => { if (!open) { setResponderDialog({ open: false, email: null }); setAdjuntosRespuesta([]); } }}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] h-[92vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Send className="h-5 w-5 text-teal-600" />
              Responder al Cliente
            </DialogTitle>
          </DialogHeader>
          {responderDialog.email && (
            <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
              {/* Info bar - email original */}
              <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">{responderDialog.email.asunto}</p>
                    <p className="text-gray-500">De: {responderDialog.email.remitente_nombre || responderDialog.email.remitente} &lt;{responderDialog.email.remitente}&gt;</p>
                  </div>
                  {responderDialog.email.ia_tipo_consulta && (
                    <Badge className="text-xs bg-cyan-50 text-cyan-700 border border-cyan-200">{responderDialog.email.ia_tipo_consulta}</Badge>
                  )}
                </div>
                {responderDialog.email.ia_resumen && (
                  <p className="text-xs text-gray-400 mt-1 border-t pt-1">{responderDialog.email.ia_resumen}</p>
                )}
              </div>

              {/* Via de respuesta */}
              <div>
                <Label className="font-medium">Via de respuesta</Label>
                <Select value={responderForm.tipo_respuesta} onValueChange={(v) => setResponderForm(f => ({ ...f, tipo_respuesta: v }))}>
                  <SelectTrigger data-testid="responder-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email"><Mail className="h-4 w-4 inline mr-1" />Email</SelectItem>
                    <SelectItem value="telefono"><Phone className="h-4 w-4 inline mr-1" />Telefono</SelectItem>
                    <SelectItem value="whatsapp"><MessageSquare className="h-4 w-4 inline mr-1" />WhatsApp</SelectItem>
                    <SelectItem value="sms"><MessageSquare className="h-4 w-4 inline mr-1" />SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ===== EMAIL COMPOSITOR ===== */}
              {responderForm.tipo_respuesta === 'email' && (
                <div className="space-y-3 border rounded-lg p-4 bg-white">
                  {/* Plantillas */}
                  {plantillas.length > 0 && (
                    <div>
                      <Label className="text-xs text-gray-500">Cargar plantilla (opcional)</Label>
                      <Select value="" onValueChange={(key) => {
                        const tpl = plantillas.find(p => p.key === key);
                        if (tpl) {
                          const nombre = responderDialog.email?.remitente_nombre || responderDialog.email?.remitente || 'Sr./Sra.';
                          const asuntoOrig = responderDialog.email?.asunto || '';
                          setResponderForm(f => ({
                            ...f,
                            asunto: tpl.asunto.replace(/\{\{asunto_original\}\}/g, asuntoOrig).replace(/\{\{cliente_nombre\}\}/g, nombre),
                            cuerpo: tpl.cuerpo.replace(/\{\{cliente_nombre\}\}/g, nombre).replace(/\{\{asunto_original\}\}/g, asuntoOrig),
                          }));
                        }
                      }}>
                        <SelectTrigger data-testid="plantilla-select"><SelectValue placeholder="Seleccionar plantilla..." /></SelectTrigger>
                        <SelectContent>
                          {plantillas.map(p => (
                            <SelectItem key={p.key} value={p.key}>{p.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label>Destinatario *</Label>
                    <Input value={responderForm.destinatario_email} onChange={(e) => setResponderForm(f => ({ ...f, destinatario_email: e.target.value }))} placeholder="email@cliente.com" data-testid="responder-destinatario" />
                  </div>
                  <div>
                    <Label>Asunto *</Label>
                    <Input value={responderForm.asunto} onChange={(e) => setResponderForm(f => ({ ...f, asunto: e.target.value }))} placeholder="Re: ..." data-testid="responder-asunto" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Cuerpo del email *</Label>
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setResponderForm(f => ({ ...f, cuerpo: '' }))}
                      >
                        Limpiar texto
                      </button>
                    </div>
                    <RichTextEditor
                      value={responderForm.cuerpo}
                      onChange={(value) => setResponderForm(f => ({ ...f, cuerpo: value }))}
                      placeholder="Escriba el contenido del email..."
                    />
                    <p className="text-xs text-gray-400 mt-1">El email se enviara con formato HTML con la cabecera del despacho.</p>
                  </div>

                  {/* Adjuntos */}
                  <div>
                    <Label className="flex items-center gap-1">
                      <Paperclip className="h-3.5 w-3.5" /> Adjuntos
                    </Label>
                    <div className="mt-1 space-y-2">
                      {adjuntosRespuesta.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {adjuntosRespuesta.map((adj, idx) => (
                            <div key={idx} className="flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1">
                              <Paperclip className="h-3 w-3 text-blue-500" />
                              <span>{adj.nombre}</span>
                              <span className="text-gray-400">({(adj.tamano / 1024).toFixed(0)}KB)</span>
                              <button className="text-red-400 hover:text-red-600 ml-1" onClick={() => setAdjuntosRespuesta(prev => prev.filter((_, i) => i !== idx))}>
                                <XCircle className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <label className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 cursor-pointer border border-dashed border-blue-300 rounded px-3 py-1.5 hover:bg-blue-50 transition-colors">
                        {subiendoAdjunto ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                        {subiendoAdjunto ? 'Subiendo...' : 'Adjuntar archivo'}
                        <input type="file" multiple className="hidden" onChange={handleUploadAdjunto} disabled={subiendoAdjunto} />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== TELEFONO / WHATSAPP / SMS ===== */}
              {responderForm.tipo_respuesta !== 'email' && (
                <div className="space-y-3 border rounded-lg p-4 bg-white">
                  <div>
                    <Label>Telefono de contacto</Label>
                    <Input value={responderForm.telefono_contacto} onChange={(e) => setResponderForm(f => ({ ...f, telefono_contacto: e.target.value }))} placeholder="612 345 678" />
                  </div>
                  {responderForm.tipo_respuesta === 'telefono' && (
                    <div>
                      <Label>Duracion de la llamada</Label>
                      <Input placeholder="00:05:00" value={responderForm.duracion_llamada} onChange={(e) => setResponderForm(f => ({ ...f, duracion_llamada: e.target.value }))} />
                    </div>
                  )}
                  <div>
                    <Label>Descripcion de la gestion realizada *</Label>
                    <Textarea
                      value={responderForm.cuerpo}
                      onChange={(e) => setResponderForm(f => ({ ...f, cuerpo: e.target.value }))}
                      rows={6}
                      placeholder="Describa la gestion realizada con el cliente..."
                      data-testid="responder-cuerpo"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setResponderDialog({ open: false, email: null }); setAdjuntosRespuesta([]); }}>Cancelar</Button>
            <Button onClick={handleResponder} disabled={respondiendo || !responderForm.cuerpo} data-testid="responder-confirm-btn">
              {respondiendo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              {responderForm.tipo_respuesta === 'email' ? 'Enviar Email' : 'Registrar Gestion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== FETCH DIALOG ===== */}
      <Dialog open={fetchDialog} onOpenChange={setFetchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" /> Descargar Emails de Clientes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Seleccione el rango de fechas para descargar solo los emails de ese periodo.
            </p>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => {
                const hoy = new Date().toISOString().split('T')[0];
                setFetchForm(f => ({...f, fecha_desde: hoy, fecha_hasta: hoy}));
              }} data-testid="fetch-hoy-btn">
                <Calendar className="h-3 w-3 mr-1" /> Hoy
              </Button>
              <Button variant="outline" size="sm" onClick={() => {
                const hoy = new Date();
                const ayer = new Date(hoy); ayer.setDate(ayer.getDate() - 1);
                setFetchForm(f => ({...f, fecha_desde: ayer.toISOString().split('T')[0], fecha_hasta: hoy.toISOString().split('T')[0]}));
              }}>
                Ayer + Hoy
              </Button>
              <Button variant="outline" size="sm" onClick={() => {
                const hoy = new Date();
                const semana = new Date(hoy); semana.setDate(semana.getDate() - 7);
                setFetchForm(f => ({...f, fecha_desde: semana.toISOString().split('T')[0], fecha_hasta: hoy.toISOString().split('T')[0]}));
              }}>
                Ultima semana
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha desde *</Label>
                <Input type="date" value={fetchForm.fecha_desde} onChange={(e) => setFetchForm(f => ({...f, fecha_desde: e.target.value}))} data-testid="fetch-fecha-desde" />
              </div>
              <div>
                <Label>Fecha hasta *</Label>
                <Input type="date" value={fetchForm.fecha_hasta} onChange={(e) => setFetchForm(f => ({...f, fecha_hasta: e.target.value}))} data-testid="fetch-fecha-hasta" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hora desde <span className="text-xs text-gray-400">(opcional)</span></Label>
                <Input type="time" value={fetchForm.hora_desde} onChange={(e) => setFetchForm(f => ({...f, hora_desde: e.target.value}))} data-testid="fetch-hora-desde" />
              </div>
              <div>
                <Label>Hora hasta <span className="text-xs text-gray-400">(opcional)</span></Label>
                <Input type="time" value={fetchForm.hora_hasta} onChange={(e) => setFetchForm(f => ({...f, hora_hasta: e.target.value}))} data-testid="fetch-hora-hasta" />
              </div>
            </div>

            <div>
              <Label>Filtrar por remitente <span className="text-xs text-gray-400">(opcional)</span></Label>
              <Input placeholder="ej: cliente@dominio.com" value={fetchForm.remitente || ''} onChange={(e) => setFetchForm(f => ({...f, remitente: e.target.value}))} data-testid="fetch-remitente" />
            </div>

            <div>
              <Label>Limite de emails a descargar</Label>
              <Input type="number" value={fetchForm.limit} onChange={(e) => setFetchForm(f => ({...f, limit: parseInt(e.target.value) || 50}))} min={1} max={500} data-testid="fetch-limit" />
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>Los emails ya descargados se detectaran como duplicados y no se importaran de nuevo.</span>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setFetchDialog(false)}>Cancelar</Button>
              <Button onClick={executeFetch} disabled={fetching} data-testid="execute-fetch-btn">
                {fetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                Descargar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CONFIG DIALOG ===== */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" /> Configuracion - Emails Clientes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la cuenta</Label>
              <Input value={configForm.nombre} onChange={(e) => setConfigForm(f => ({...f, nombre: e.target.value}))} placeholder="Ej: Emails Clientes" data-testid="config-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Servidor IMAP *</Label>
                <Input value={configForm.imap_host} onChange={(e) => setConfigForm(f => ({...f, imap_host: e.target.value}))} placeholder="mail.lexduo.cat" data-testid="config-imap-host" />
              </div>
              <div>
                <Label>Puerto</Label>
                <Input type="number" value={configForm.imap_port} onChange={(e) => setConfigForm(f => ({...f, imap_port: parseInt(e.target.value) || 993}))} data-testid="config-imap-port" />
              </div>
            </div>
            <div>
              <Label>Usuario (email) *</Label>
              <Input value={configForm.imap_user} onChange={(e) => setConfigForm(f => ({...f, imap_user: e.target.value}))} placeholder="info@lexduo.cat" data-testid="config-imap-user" />
            </div>
            <div>
              <Label>Contrasena *</Label>
              <Input type="password" value={configForm.imap_password} onChange={(e) => setConfigForm(f => ({...f, imap_password: e.target.value}))} placeholder={config ? '(sin cambios si vacio)' : 'Contrasena del email'} data-testid="config-imap-password" />
            </div>
            <div>
              <Label>Carpeta IMAP</Label>
              <Input value={configForm.imap_carpeta} onChange={(e) => setConfigForm(f => ({...f, imap_carpeta: e.target.value}))} placeholder="INBOX" data-testid="config-imap-carpeta" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cli_imap_ssl" checked={configForm.imap_use_ssl} onChange={(e) => setConfigForm(f => ({...f, imap_use_ssl: e.target.checked}))} />
              <Label htmlFor="cli_imap_ssl">Usar SSL</Label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cli_auto_process" checked={configForm.auto_process_ia} onChange={(e) => setConfigForm(f => ({...f, auto_process_ia: e.target.checked}))} />
              <Label htmlFor="cli_auto_process">Procesar automaticamente con IA al descargar</Label>
            </div>
            <p className="text-xs text-gray-400 -mt-2 ml-6">
              Si se activa, tras descargar emails se lanzara automaticamente el analisis IA.
            </p>

            <div className="border-t pt-3 mt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Descarga automatica programada</h4>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="cli_auto_fetch" checked={configForm.auto_fetch} onChange={(e) => setConfigForm(f => ({...f, auto_fetch: e.target.checked}))} />
                <Label htmlFor="cli_auto_fetch">Activar descarga automatica periodica</Label>
              </div>
              {configForm.auto_fetch && (
                <div className="mt-2 ml-6">
                  <Label>Intervalo (minutos)</Label>
                  <Input type="number" className="w-32" value={configForm.auto_fetch_intervalo_min} onChange={(e) => setConfigForm(f => ({...f, auto_fetch_intervalo_min: parseInt(e.target.value) || 15}))} min={5} max={1440} />
                  <p className="text-xs text-gray-400 mt-1">El sistema descargara emails automaticamente cada {configForm.auto_fetch_intervalo_min} minutos</p>
                </div>
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={handleTestConnection} disabled={testingConnection} data-testid="test-connection-btn">
                {testingConnection ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Probar Conexion
              </Button>
              <Button onClick={handleSaveConfig} disabled={savingConfig} data-testid="save-config-btn">
                {savingConfig ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Guardar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DISCARD CONFIRM DIALOG ===== */}
      <Dialog open={!!discardConfirm} onOpenChange={(open) => !open && setDiscardConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Descartar email</DialogTitle></DialogHeader>
          <p className="text-sm">El email se movera a descartados. Esta seguro?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={() => handleDiscard(discardConfirm)} data-testid="discard-confirm-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
